/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/* ═══════════════════════════════════════════════════════════
   Theme API — syncs custom themes with the Django backend
   and caches in localStorage for instant hydration.
   ═══════════════════════════════════════════════════════════ */

import axios from 'axios';
import API_BASE_URL from './config';

const api = axios.create({ baseURL: `${API_BASE_URL}/api/user` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    // Don't auto-logout on theme failures — silently degrade
    console.error('[ThemeAPI]', err?.response?.status, err?.message);
    return Promise.reject(err);
  },
);

/* ── localStorage cache keys ── */
const LS_PLATFORM = 'paxia-platform-themes';
const LS_PERSONAL = 'paxia-personal-themes';   // replaces old paxia-custom-*
const LS_PREF     = 'paxia-theme-preference';

/* ── Cache helpers ── */
function cacheRead(key) {
  try { return JSON.parse(localStorage.getItem(key)) || null; }
  catch { return null; }
}
function cacheWrite(key, val) {
  localStorage.setItem(key, JSON.stringify(val));
}

/* ═══════════════════════════════════════════════════════════
   Hydration — single call to get everything on login/load
   ═══════════════════════════════════════════════════════════ */

/**
 * Fetch all themes + preference from backend and cache locally.
 * Returns { platformThemes, personalThemes, preference } or cached fallback.
 */
export async function fetchAllThemes() {
  try {
    const data = await api.get('/themes/');
    const result = {
      platformThemes: data.platform_themes || [],
      personalThemes: data.personal_themes || [],
      preference: data.preference || { active_style: 'standard', active_color_scheme: 'light' },
    };
    // Cache for offline / fast reload
    cacheWrite(LS_PLATFORM, result.platformThemes);
    cacheWrite(LS_PERSONAL, result.personalThemes);
    cacheWrite(LS_PREF, result.preference);
    return result;
  } catch {
    // Fall back to cached data
    return {
      platformThemes: cacheRead(LS_PLATFORM) || [],
      personalThemes: cacheRead(LS_PERSONAL) || [],
      preference: cacheRead(LS_PREF) || { active_style: 'standard', active_color_scheme: 'light' },
    };
  }
}

/**
 * Get cached themes synchronously (for initial render before API responds).
 */
export function getCachedThemes() {
  return {
    platformThemes: cacheRead(LS_PLATFORM) || [],
    personalThemes: cacheRead(LS_PERSONAL) || [],
    preference: cacheRead(LS_PREF) || { active_style: 'standard', active_color_scheme: 'light' },
  };
}

/* ═══════════════════════════════════════════════════════════
   Platform themes (admin)
   ═══════════════════════════════════════════════════════════ */

export async function createPlatformTheme(payload) {
  const result = await api.post('/themes/platform/', payload);
  return result;
}

export async function updatePlatformTheme(themeId, payload) {
  const result = await api.put(`/themes/platform/${themeId}/`, payload);
  return result;
}

export async function deletePlatformTheme(themeId) {
  await api.delete(`/themes/platform/${themeId}/`);
}

/* ═══════════════════════════════════════════════════════════
   Personal themes (per-user)
   ═══════════════════════════════════════════════════════════ */

export async function createPersonalTheme(payload) {
  const result = await api.post('/themes/personal/', payload);
  return result;
}

export async function updatePersonalTheme(themeId, payload) {
  const result = await api.put(`/themes/personal/${themeId}/`, payload);
  return result;
}

export async function deletePersonalTheme(themeId) {
  await api.delete(`/themes/personal/${themeId}/`);
}

/* ═══════════════════════════════════════════════════════════
   Preference — save active selections
   ═══════════════════════════════════════════════════════════ */

export async function saveThemePreference({ activeStyle, activeColorScheme, backgroundVideoUrl, backgroundVideoOverlay, contentBgOpacity }) {
  const pref = {
    active_style: activeStyle,
    active_color_scheme: activeColorScheme,
    background_video_url: backgroundVideoUrl || '',
    background_video_overlay: backgroundVideoOverlay ?? 0.7,
    content_bg_opacity: contentBgOpacity ?? 0.85,
  };
  console.log('[ThemeAPI] saveThemePreference called →', pref);
  try {
    const result = await api.put('/themes/preference/', pref);
    console.log('[ThemeAPI] Preference saved successfully');
    cacheWrite(LS_PREF, pref);
    return result;
  } catch (err) {
    console.error('[ThemeAPI] Failed to save preference:', err?.response?.status, err?.response?.data, err?.message);
  }
}

/* ═══════════════════════════════════════════════════════════
   Video upload
   ═══════════════════════════════════════════════════════════ */

/**
 * Upload a video file for use as a theme background.
 * @param {File} file — the video File object from an <input>
 * @param {(pct: number) => void} [onProgress] — optional progress callback (0-100)
 * @returns {{ id, url, original_name, size_bytes }}
 */
export async function uploadThemeVideo(file, onProgress) {
  const form = new FormData();
  form.append('file', file);
  const result = await api.post('/themes/videos/upload/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1)))
      : undefined,
  });
  return result;
}

/**
 * List the user's previously uploaded theme videos.
 */
export async function listThemeVideos() {
  return api.get('/themes/videos/');
}

/**
 * Delete an uploaded theme video by ID.
 * @param {number} id — the video upload ID
 */
export async function deleteThemeVideo(id) {
  return api.delete(`/themes/videos/${id}/`);
}
